import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import CollapsiblePanel from './CollapsiblePanel';

export default function FixedCostCategories({ defaultCollapsed, onRefresh }) {
  const [categories, setCategories] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoryToAdd, setCategoryToAdd] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.getFixedCostCategories();
      setCategories(data.categories);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.getCategoriesList().then(setAvailableCategories).catch(() => setAvailableCategories([]));
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.updateFixedCostCategories(next);
      setCategories(next);
      onRefresh?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const addCategory = (category) => {
    if (!category || (categories || []).includes(category)) return;
    save([...(categories || []), category]);
    setCategoryToAdd('');
  };

  const removeCategory = (category) => {
    save((categories || []).filter((c) => c !== category));
  };

  if (!categories) return null;

  return (
    <CollapsiblePanel title="🔒 Fixed Cost Categories" defaultCollapsed={defaultCollapsed}>
      <p style={styles.helpText}>
        Categories treated as fixed/recurring cost-of-living spend in the Spending Breakdown view (Overview) and AI
        Recap - shown separately from one-off discretionary spending, and never suggested as something to cut back
        on. A category also counts as fixed automatically if a specific charge in it is detected as recurring,
        regardless of this list - this is for categories that should ALWAYS count as fixed, like Tax.
      </p>

      {categories.length > 0 && (
        <div style={styles.chipsRow}>
          {categories.map((cat) => (
            <span key={cat} style={styles.chip}>
              {cat}
              <button
                style={styles.chipRemove}
                onClick={() => removeCategory(cat)}
                disabled={saving}
                title={`Remove ${cat}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <select
        style={styles.categoryDropdown}
        value={categoryToAdd}
        onChange={(e) => addCategory(e.target.value)}
        disabled={saving}
      >
        <option value="">+ Add a category as fixed...</option>
        {availableCategories
          .filter((c) => !categories.includes(c))
          .map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
      </select>
    </CollapsiblePanel>
  );
}

const styles = {
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1rem',
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '0.85rem',
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    background: 'rgba(0, 212, 170, 0.1)',
    border: '1px solid #00d4aa',
    borderRadius: '999px',
    padding: '3px 6px 3px 12px',
    color: '#00d4aa',
    fontSize: '0.72rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  chipRemove: {
    background: 'transparent',
    border: 'none',
    color: '#00d4aa',
    fontSize: '0.9rem',
    lineHeight: 1,
    cursor: 'pointer',
    padding: '2px 4px',
  },
  categoryDropdown: {
    width: '100%',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '8px 10px',
    color: '#fff',
    fontSize: '0.78rem',
    fontFamily: "'Syne', sans-serif",
  },
};
